import LottieImport from 'lottie-react';
// TODO: Remove when Lottie fixes their package
const Lottie = LottieImport.default ?? LottieImport;
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import dotsIcon from '@/assets/animated/dots-icon.json';
import { merge } from '@/components';
import { Flex, Text, Tooltip, TooltipTrigger } from '@/components/core';
import {
  AlertFilledIcon,
  ArchiveIcon,
  ArrowShortIcon,
  CheckmarkFilledIcon,
  WaitingFilledIcon,
} from '@/components/icons';
import { CLASSNAME_SHINE, STATUS_TO_NAME } from '@/constants';
import { toAgentRunId, useGetAgentRun } from '@/hooks/api';
import { capitalize } from '@/utils';

export type AgentRunStatusProps = {
  agentId?: string;
  runId?: string;
  compact?: boolean;
};

const IAgentRunStatus = ({ agentId, runId, compact }: AgentRunStatusProps) => {
  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId));

  const { badge, tooltipText } = useMemo(() => {
    let badgeTextClassName: string | undefined;
    let badgeIcon: React.ReactNode;
    const badgeClassName = 'size-3';
    let tooltipText: string;

    switch (run?.status) {
      case 'archived':
        badgeIcon = <ArchiveIcon className={badgeClassName} />;
        tooltipText = 'This run has been archived.';
        break;
      case 'completed':
      case 'exported':
        badgeTextClassName = 'text-success-900';
        badgeIcon = <CheckmarkFilledIcon variant="success" className={badgeClassName} />;
        tooltipText = 'This run has been completed successfully. See events for details.';
        break;
      case 'error':
        badgeTextClassName = 'text-error-900';
        badgeIcon = <AlertFilledIcon variant="error" className={badgeClassName} />;
        tooltipText = 'An error occurred during this run. See events for details.';
        break;
      case 'ready-for-review':
        badgeTextClassName = 'text-warning-900';
        badgeIcon = (
          <div className="flex size-4 items-center justify-center">
            <div className="relative inline-flex">
              <div className="bg-warning-500 size-1.5 rounded-full" />
              <div className="bg-warning-500 absolute top-0 left-0 size-1.5 animate-ping rounded-full" />
              <div className="bg-warning-500 absolute top-0 left-0 size-1.5 animate-pulse rounded-full" />
            </div>
          </div>
        );
        tooltipText = 'The AI response needs review. A human review is needed before it can be exported.';
        break;
      case 'review-in-progress':
        badgeTextClassName = 'text-primary-900';
        badgeIcon = <WaitingFilledIcon variant="primary" className={badgeClassName} />;
        tooltipText = 'This run is currently being reviewed.';
        break;
      case 'review-completed':
      case 'succeeded-predictions':
        badgeTextClassName = 'text-success-900';
        badgeIcon = <CheckmarkFilledIcon variant="success" className={badgeClassName} />;
        tooltipText = 'This run has a legacy status. See events for details.';
        break;
      case 'pending-export':
      case 'pending-predictions':
        badgeTextClassName = 'text-warning-900';
        badgeIcon = <AlertFilledIcon variant="warning" className={badgeClassName} />;
        tooltipText = 'This run is stuck, rerun from start to unstuck.';
        break;
      default:
        badgeTextClassName = CLASSNAME_SHINE;
        badgeIcon = <Lottie animationData={dotsIcon} className={badgeClassName} />;
        tooltipText = 'The agent is processing the document.';
    }

    const badge = compact ? (
      badgeIcon
    ) : (
      <Flex
        role="button"
        tabIndex={-1}
        variant="secondary"
        className="flex items-center gap-1.5 rounded-sm px-1.5 py-0.5"
      >
        {badgeIcon}
        <Text className={badgeTextClassName} size="xs">
          {STATUS_TO_NAME[run?.status ?? ''] ?? capitalize(run?.status)}
        </Text>
        <ArrowShortIcon
          className={merge(
            'hidden size-3 text-gray-500',
            run?.status === 'ready-for-review' && 'group-hover/row:block'
          )}
          rotation="right"
        />
      </Flex>
    );

    return { badge, tooltipText };
  }, [compact, run]);

  return (
    <TooltipTrigger>
      <Tooltip variant="primary" className="gap-xs flex w-48 flex-col">
        <Text>{tooltipText}</Text>
      </Tooltip>
      <Focusable>{badge}</Focusable>
    </TooltipTrigger>
  );
};

export const AgentRunStatus = memo(IAgentRunStatus) as typeof IAgentRunStatus;
